import {Location} from 'history';
import React, {useEffect, useState} from 'react';
import {Prompt} from 'react-router-dom';
import {IonAlert} from "@ionic/react";
import {useTranslation} from "react-i18next";

interface Props {
    when?: boolean | undefined;
    navigate: (path: string) => void;
    shouldBlockNavigation: (location: Location) => boolean;
    isOpen?: boolean;
    onClose?: () => void;
}

const PageLeavingGuard = ({
                              when,
                              navigate,
                              shouldBlockNavigation,
                              isOpen: externalIsOpen,
                              onClose,
                          }: Props) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [lastLocation, setLastLocation] = useState<Location | null>(null);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);

    const closeModal = () => {
        setModalVisible(false);
        if (onClose) onClose();
    };

    const handleBlockedNavigation = (nextLocation: Location): boolean => {
        if (!confirmedNavigation && shouldBlockNavigation(nextLocation)) {
            setModalVisible(true);
            setLastLocation(nextLocation);
            return false;
        }
        return true;
    };

    const handleConfirmNavigationClick = () => {
        setModalVisible(false);
        if (onClose) onClose();
        // Call navigate() synchronously right here instead of deferring it to a
        // useEffect keyed on confirmedNavigation. Deferring it meant the actual
        // history.goBack() only ran on the next passive-effect pass, well after
        // this click handler returned - a gap that made whether it fired at all
        // depend on unrelated renders/effects happening to line up correctly
        // (worse the busier the page, e.g. reports with a specification running
        // extra background checks). Setting confirmedNavigation first still lets
        // consumers without a ref-based bypass avoid Prompt re-blocking this same
        // navigate() call, since React re-registers the Prompt's blocker
        // synchronously as part of this state update before the next line runs.
        setConfirmedNavigation(true);
        navigate(lastLocation?.pathname ?? '');
        setConfirmedNavigation(false);
    };

    useEffect(() => {
        if (externalIsOpen) {
            setConfirmedNavigation(false);
            setLastLocation(null);
        }
    }, [externalIsOpen]);

    const {t} = useTranslation();

    return (
        <>
            <Prompt when={when} message={handleBlockedNavigation}/>
            <IonAlert
                isOpen={modalVisible || (externalIsOpen ?? false)}
                onDidDismiss={() => closeModal()}
                header={t('reportPage.backAlert.title')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {

                        }
                    },
                    {
                        text: t('reportPage.backAlert.discard'),
                        handler: async () => {
                            handleConfirmNavigationClick();
                        }
                    }
                ]}
            />
        </>
    );
};
export default PageLeavingGuard;
